import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/firebaseConfig";

import Login from "./Login";
import Register from "./Register";
import RegisterTrial from "./RegisterTrial";
import MainLayout from "./layouts/MainLayout";
import AppRoutes from "./AppRoutes";
import OrganizationLayout from "./organizations/OrganizationLayout";
import ProjectLayout from "./projects/ProjectLayout";
import AdminLayout from "./admin/AdminLayout";
import PublicEvaluationLayout from "./public/PublicEvaluationLayout";
import Assessment2PublicLayout from "./assessment2/public/PublicEvaluationLayout"; // Assessment 2 (PLAN_ASSESSMENT2.md)

import useStore from "./config/store";
import { setupAxiosInterceptors } from "./config/axiosInterceptors";
import "./App.css";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import theme from "./theme";
import GlobalAlert from "./layouts/GlobalAlert";
import { useTranslation } from 'react-i18next';
import MyOrganizationLayout from "./projects/MyOrganizationLayout";
import PaymentSuccess from './projects/PaymentSuccess';
import PaymentCancel from './projects/PaymentCancel';
import PublicDiscTest from './public/PublicDiscTest';
import PublicReasoningTest from './public/PublicReasoningTest';
import PublicSalesAssessment from './public/PublicSalesAssessment';
import PortalLayout from './portal/PortalLayout';
import PortalDocumentsScreen from './portal/PortalDocumentsScreen';
import JobBoardLayout from './jobBoard/JobBoardLayout';
import PublicCaptationChat from './captation/PublicCaptationChat';
import PublicFreeCaptationChat from './captation/PublicFreeCaptationChat';
import PrivacyPolicy from './legal/PrivacyPolicy';
import ResetPasswordScreen from './user/ResetPasswordScreen';
import { getHostBoardSlug } from './jobBoard/hostSlug';

// Subdominio {slug}.amandahr.ai → la raíz muestra la web de empleos de esa org
const hostBoardSlug = getHostBoardSlug();

// Interceptor global: 401 TOKEN_INVALID → cierra sesión y redirige a /login
setupAxiosInterceptors();

function App() {
  const [loading, setLoading] = useState(true);
  const firebaseUserRef = useRef(null);
  const { i18n } = useTranslation();

  const {
    setAuthToken,
    refreshAuthToken,
    resetStore,
    user,
    organization,
    project,
    language,
  } = useStore();

  useEffect(() => {
    let interval;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const token = await currentUser.getIdToken();
        firebaseUserRef.current = currentUser;
        setAuthToken(token);

        interval = setInterval(async () => {
          await refreshAuthToken(firebaseUserRef.current);
        }, 5 * 60 * 1000);
      } else {
        firebaseUserRef.current = null;
        resetStore();

        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (interval) clearInterval(interval);
    };
  }, [setAuthToken, resetStore, refreshAuthToken]);

  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalAlert />

      {/* Backdrop de carga */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column'
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {!loading && (
        <Router>
          <Routes>
            <Route path="/subscription/success" element={<PaymentSuccess />} />
            <Route path="/subscription/cancel" element={<PaymentCancel />} />

            {/* RUTAS PÚBLICAS - Se evalúan PRIMERO, antes de la autenticación */}
            <Route path="/evaluation-public/:responseId/:language" element={<PublicEvaluationLayout />} />
            <Route path="/evaluation-public/:responseId" element={<PublicEvaluationLayout />} />
            {/* Assessment 2 (PLAN_ASSESSMENT2.md) */}
            <Route path="/assessment2-public/:responseId/:language" element={<Assessment2PublicLayout />} />
            <Route path="/assessment2-public/:responseId" element={<Assessment2PublicLayout />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-trial" element={<RegisterTrial />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/disc-test/:testId" element={<PublicDiscTest />} />
            <Route path="/reasoning-test/:testId/:language" element={<PublicReasoningTest />} />
            <Route path="/reasoning-test/:testId" element={<PublicReasoningTest />} />
            <Route path="/public/sales-assessment/:testId" element={<PublicSalesAssessment />} />

            {/* PORTAL CANDIDATOS */}
            {hostBoardSlug && (
              <Route path="/" element={<JobBoardLayout hostSlug={hostBoardSlug} />} />
            )}
            <Route path="/empleos" element={<JobBoardLayout />} />
            <Route path="/empleos/:slug" element={<JobBoardLayout />} />
            {/* Página pública de una feria: solo sus vacantes (PLAN_FERIAS) */}
            <Route path="/feria/:fairSlug" element={<JobBoardLayout />} />
            {/* Página pública de una vacante: el link que entrega el ATS. La web
                de empleos de su organización con esa vacante abierta, aunque
                esté cerrada; el correo se pide recién al postular (2026-10-25) */}
            <Route path="/vacante/:positionId" element={<JobBoardLayout />} />
            <Route path="/portal/apply/:positionId" element={<PortalLayout />} />
            <Route path="/portal/documentos/:token" element={<PortalDocumentsScreen />} />
            <Route path="/postular/:positionId" element={<PublicCaptationChat />} />
            {/* Chat libre de captación: /chat (global) · /chat/:slug (por organización)
                En el subdominio del job board, /chat hereda el slug del host */}
            {/* Página legal pública (privacidad + condiciones + eliminación de
                datos) — URLs exigidas por la app de Meta para el modo Activo */}
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/chat" element={<PublicFreeCaptationChat hostSlug={hostBoardSlug} />} />
            <Route path="/chat/:slug" element={<PublicFreeCaptationChat />} />
            <Route path="/portal" element={<PortalLayout />} />

            {/* RUTAS PRIVADAS - Con toda la lógica de autenticación existente */}
            {!user ? (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : ["super-admin", "admin-grafton"].includes(user.rol) ? (
              <>
                {/* Mantén ambas rutas para super-admin/admin-grafton */}
                <Route path="/admin" element={<AdminLayout />} />
                <Route path="/myOrganization" element={<MyOrganizationLayout />} />

                {project ? (
                  // Si ya hay project, habilita TODAS las rutas de la app desde aquí
                  <Route
                    path="*"
                    element={
                      <MainLayout>
                        <AppRoutes />
                      </MainLayout>
                    }
                  />
                ) : (
                  // Si no hay project, conserva tu fallback original
                  <Route
                    path="*"
                    element={<Navigate to={organization ? "/myOrganization" : "/admin"} replace />}
                  />
                )}
              </>
            ) : ["admin"].includes(user.rol) ? (
              <>
                <Route path="/myOrganization" element={<MyOrganizationLayout />} />

                {project ? (
                  // Con project listo, deja pasar a la app
                  <Route
                    path="*"
                    element={
                      <MainLayout>
                        <AppRoutes />
                      </MainLayout>
                    }
                  />
                ) : (
                  // Sin project, te quedas en MyOrganization
                  <Route path="*" element={<Navigate to="/myOrganization" replace />} />
                )}
              </>
            ) : !organization ? (
              <>
                <Route path="/organizations" element={<OrganizationLayout />} />
                <Route path="*" element={<Navigate to="/organizations" replace />} />
              </>
            ) : !project ? (
              <>
                <Route path="/myOrganization" element={<ProjectLayout />} />
                <Route path="*" element={<Navigate to="/myOrganization" replace />} />
              </>
            ) : (
              <Route
                path="*"
                element={
                  <MainLayout>
                    <AppRoutes />
                  </MainLayout>
                }
              />
            )}
          </Routes>
        </Router>
      )}
    </ThemeProvider>
  );
}

export default App;